/**
 * GET client for owner question-answer trace list and detail reads.
 */

import {
  buildCognitoryQuestionAnswerTraceDetailPath,
  buildCognitoryQuestionAnswerTraceListPath,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";
import {
  rebuildCognitoryActivityDetailPayload,
  rebuildCognitoryActivityListPayload,
  type CognitoryActivityDetailView,
  type CognitoryActivitySummaryView,
} from "./cognitoryQuestionAnswerTraceOwnerReadRebuild";

export type CognitoryActivityListClientResult =
  | {
      kind: "success";
      supportReference: string;
      cognitoryId: string;
      limit: number;
      items: CognitoryActivitySummaryView[];
      nextCursor: string | null;
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "invalid_request"
        | "temporary_failure"
        | "malformed_success";
      supportReference: string | null;
    };

export type CognitoryActivityDetailClientResult =
  | {
      kind: "success";
      supportReference: string;
      trace: CognitoryActivityDetailView;
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "invalid_request"
        | "temporary_failure"
        | "malformed_success";
      supportReference: string | null;
    };

function readJsonRequestId(body: unknown): string | null {
  if (!body || typeof body !== "object") {
    return null;
  }
  const requestId = (body as { requestId?: unknown }).requestId;
  return typeof requestId === "string" && requestId.length > 0
    ? requestId
    : null;
}

async function readResponseBody(response: Response): Promise<unknown | null> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export async function fetchCognitoryActivityTraceList(input: {
  cognitoryId: string;
  cursor?: string | null;
  limit?: number;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<CognitoryActivityListClientResult> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const token =
    input.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated", supportReference: null };
  }

  const base = input.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  const url = new URL(
    `${base}${buildCognitoryQuestionAnswerTraceListPath(input.cognitoryId)}`,
  );
  if (input.limit !== undefined) {
    url.searchParams.set("limit", String(input.limit));
  }
  if (input.cursor) {
    url.searchParams.set("cursor", input.cursor);
  }

  let response: Response;
  try {
    response = await fetchImpl(url.toString(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      cache: "no-store",
    });
  } catch {
    return { kind: "temporary_failure", supportReference: null };
  }

  const body = await readResponseBody(response);

  if (response.status === 401) {
    return {
      kind: "unauthenticated",
      supportReference: readJsonRequestId(body),
    };
  }
  if (response.status === 403) {
    return {
      kind: "forbidden",
      supportReference: readJsonRequestId(body),
    };
  }
  if (response.status === 400) {
    return {
      kind: "invalid_request",
      supportReference: readJsonRequestId(body),
    };
  }
  if (response.status === 503) {
    return {
      kind: "temporary_failure",
      supportReference: readJsonRequestId(body),
    };
  }
  if (response.status !== 200) {
    return {
      kind: "temporary_failure",
      supportReference: readJsonRequestId(body),
    };
  }
  if (body === null) {
    return { kind: "malformed_success", supportReference: null };
  }

  const rebuilt = rebuildCognitoryActivityListPayload({
    body,
    expectedCognitoryId: input.cognitoryId,
  });
  if (rebuilt.kind === "malformed_success") {
    return rebuilt;
  }
  return rebuilt;
}

export async function fetchCognitoryActivityTraceDetail(input: {
  cognitoryId: string;
  traceRequestId: string;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<CognitoryActivityDetailClientResult> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const token =
    input.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated", supportReference: null };
  }

  const base = input.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  const url = `${base}${buildCognitoryQuestionAnswerTraceDetailPath({
    cognitoryId: input.cognitoryId,
    traceRequestId: input.traceRequestId,
  })}`;

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      cache: "no-store",
    });
  } catch {
    return { kind: "temporary_failure", supportReference: null };
  }

  const body = await readResponseBody(response);

  if (response.status === 401) {
    return {
      kind: "unauthenticated",
      supportReference: readJsonRequestId(body),
    };
  }
  if (response.status === 403) {
    return {
      kind: "forbidden",
      supportReference: readJsonRequestId(body),
    };
  }
  if (response.status === 400) {
    return {
      kind: "invalid_request",
      supportReference: readJsonRequestId(body),
    };
  }
  if (response.status === 503) {
    return {
      kind: "temporary_failure",
      supportReference: readJsonRequestId(body),
    };
  }
  if (response.status !== 200) {
    return {
      kind: "temporary_failure",
      supportReference: readJsonRequestId(body),
    };
  }
  if (body === null) {
    return { kind: "malformed_success", supportReference: null };
  }

  const rebuilt = rebuildCognitoryActivityDetailPayload({
    body,
    expectedCognitoryId: input.cognitoryId,
    expectedTraceRequestId: input.traceRequestId,
  });
  if (rebuilt.kind === "malformed_success") {
    return rebuilt;
  }
  return rebuilt;
}
